import { RenewalPlanData, RecentSearchData } from '@interfaces/giCar'
import { CONSTANTS, FORMATTING } from '@components/GICar/config'
import { getServerConfig } from '@configs/envServerConfig'
import { getUserIdCookie } from '@utils/userUtils'
import RewardServiceApi from '@services/rewardService'
import logger from '@utils/logger'

declare global {
  interface Window {
    ackoFetch?: (url: string, options?: any) => Promise<Response>
    ga?: any
    webengage?: {
      user: {
        getAnonymousId: () => string
      }
    }
    flutter_inappwebview?: {
      callHandler: (name: string, data: string) => void
    }
  }
}
export const TIMEOUT_4000 = 4000

const getDateSuffix = (d: string) => {
  const longFormatDate = new Date(d)
  const date = longFormatDate.getDate()
  let day: string
  switch (date) {
    case 1:
      day = `${date.toString()}st`
      break
    case 2:
      day = `${date.toString()}nd`
      break
    case 3:
      day = `${date.toString()}rd`
      break
    case 31:
      day = `${date.toString()}st`
      break
    default:
      day = `${date.toString()}th`
  }
  return day
}

const getDateFormat = (d: string) => {
  const longFormatDate = new Date(d)
  const day: string = getDateSuffix(d)
  const month = longFormatDate.toString().split(' ')[1]
  const year = longFormatDate.getFullYear().toString().substring(2)
  return `${day} ${month} '${year}`
}

export const getFullMonthDateFormat = (d: string) => {
  const date = new Date(d)

  const day = date.getDate() // no suffix
  const month = date.toLocaleString('en-GB', { month: 'short' })

  return `${day} ${month}`
}

export const numberOfDaysLeft = (d: string) => {
  const longFormatDate = new Date(d)
  const oneDay = 1000 * 60 * 60 * 24
  const today = new Date()
  const diffDate = (Math.round(longFormatDate.setHours(0, 0, 0, 0) - today.setHours(0, 0, 0, 0)) / oneDay).toFixed()
  if (diffDate.length === 1) {
    return '0'.concat(diffDate)
  }
  return diffDate
}

export const isAutoPlan = (plan: RenewalPlanData, product: 'car' | 'bike'): boolean => {
  const vehicleType =
    plan?.vehicle_type || plan?.product || plan?.lob_details?.product || plan?.vehicle_details?.product
  return !!vehicleType && vehicleType.toLowerCase() === product
}

export const isAutoSearch = (item: RecentSearchData, product: 'car' | 'bike'): boolean => {
  const vehicleType = item?.vehicle_type || item?.lob_details?.product
  return !!vehicleType && vehicleType.toLowerCase() === product
}

export const getDaysLeftForRenewal = (plan: RenewalPlanData): number => {
  const expiryDate = plan?.lob_details?.previous_policy_expiry_date
  if (!expiryDate) return Number.POSITIVE_INFINITY
  return parseInt(numberOfDaysLeft(expiryDate), 10)
}

export const getRecentSearchTime = (item: RecentSearchData): number => {
  const rawTime =
    item?.updated_at || item?.created_at || item?.timestamp || item?.created_on || item?.updatedAt || item?.createdAt
  const time = rawTime ? new Date(rawTime).getTime() : Number.NEGATIVE_INFINITY
  return Number.isFinite(time) ? time : Number.NEGATIVE_INFINITY
}

export const getUrgentRenewalCardsByProduct = (
  renewalPlans: RenewalPlanData[],
  product: 'car' | 'bike'
): RenewalPlanData[] => {
  const urgentRenewalPlans = renewalPlans
    .filter(plan => isAutoPlan(plan, product))
    .filter(plan => {
      const daysLeftForRenewal = getDaysLeftForRenewal(plan)
      return Number.isFinite(daysLeftForRenewal) && daysLeftForRenewal <= CONSTANTS.RENEWAL_THRESHOLD_DAYS
    })
    .sort((a, b) => getDaysLeftForRenewal(a) - getDaysLeftForRenewal(b))

  return urgentRenewalPlans.slice(0, CONSTANTS.MAX_URGENT_CARDS)
}

const hasPositiveNetPremium = (item: RecentSearchData): boolean => {
  const premium = typeof item?.net_premium === 'string' ? Number(item.net_premium) : item?.net_premium
  return Number.isFinite(premium) && premium > 0
}

export const getContinueInsuringCardsByProduct = (
  recentSearches: RecentSearchData[],
  product: 'car' | 'bike'
): RecentSearchData[] => {
  const sortedRecentSearches = [...(recentSearches || [])]
    .filter(item => isAutoSearch(item, product) && hasPositiveNetPremium(item))
    .sort((a, b) => getRecentSearchTime(b) - getRecentSearchTime(a))

  return sortedRecentSearches.slice(0, CONSTANTS.MAX_CONTINUE_CARDS)
}

export const formatPremium = (value: number | string): string | null => {
  const numberValue: number = typeof value === 'string' ? Number(value) : value
  if (!Number.isFinite(numberValue)) return null
  const formatted = new Intl.NumberFormat(FORMATTING.LOCALE, FORMATTING.CURRENCY_FORMAT_OPTIONS).format(numberValue)
  return `${FORMATTING.CURRENCY_SYMBOL}${formatted}`
}

export async function fetchMotorOrchestratorData(cookies: string, endpoint: string): Promise<any[]> {
  const envConfig = process.env.ACKO_ENV
  const baseUrl = getServerConfig(envConfig)?.motorOrchestratorInternalUrl
  const userId = getUserIdCookie(cookies)
  const numericUserId = await RewardServiceApi.getUserIdNumeric(userId)
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Cookie: cookies || '',
    source: 'carbon',
  }
  if (numericUserId) {
    headers['x-user-id'] = numericUserId
  }
  const res = await fetch(`${baseUrl}${endpoint}`, {
    method: 'GET',
    headers,
  })
  if (!res.ok) {
    const text = await res.text()
    logger.error(`Error ${res.status}: ${text}`)
    return []
  }
  const data = await res.json()
  if (!Array.isArray(data) || (data.length > 0 && typeof data[0] !== 'object')) {
    logger.error('Invalid data format:', data)
    return []
  }
  return data || []
}

export default getDateFormat
